import { useEffect, useMemo, useState } from "react";
import { api, ANLAGEN_TYPEN, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Zaehlertermine() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Date());

  const load = async () => {
    const { data } = await api.get("/zaehlertermine");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((t) => {
      map[t.termin_datum] = map[t.termin_datum] || [];
      map[t.termin_datum].push(t);
    });
    return map;
  }, [items]);

  const dayItems = grouped[fmt(selected)] || [];

  const markDone = async (t) => {
    try {
      await api.patch(`/zaehlertermine/${t.id}`, { erledigt: !t.erledigt });
      toast.success(t.erledigt ? "Wieder offen" : "Termin erledigt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/zaehlertermine/${t.id}`);
      toast.success("Termin gelöscht");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const eventDays = Object.keys(grouped).map((s) => new Date(s));

  return (
    <div className="p-8 space-y-6 animate-fade-up" data-testid="zaehlertermine-page">
      <div>
        <div className="overline">Zählertermine</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Zählertermine</h1>
        <p className="text-muted-foreground mt-1">Termine für das Setzen von Zählern verwalten.</p>
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList data-testid="view-tabs">
          <TabsTrigger value="calendar" data-testid="tab-calendar">Kalender</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">Liste</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
            <Card className="border-border">
              <CardContent className="p-4">
                <Calendar
                  mode="single"
                  selected={selected}
                  onSelect={(d) => d && setSelected(d)}
                  modifiers={{ hasEvent: eventDays }}
                  modifiersClassNames={{ hasEvent: "relative font-bold text-primary before:content-[''] before:absolute before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:w-1 before:h-1 before:rounded-full before:bg-primary" }}
                  data-testid="calendar"
                />
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="overline">Termine am</div>
                <div className="font-heading text-2xl font-bold mt-1">
                  {selected.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                </div>
                <div className="mt-4 space-y-3">
                  {dayItems.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8" data-testid="no-day-events">
                      Keine Termine an diesem Tag.
                    </div>
                  ) : dayItems.map((t) => (
                    <div key={t.id} className="border border-border rounded-md p-4 flex items-start justify-between" data-testid={`termin-${t.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{t.kunde_name}</span>
                          {t.erledigt && <Badge className="bg-emerald-600 hover:bg-emerald-600">Erledigt</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {ANLAGEN_TYPEN[t.anlagen_typ]} · {t.uhrzeit || "Ganztägig"} {t.monteur && `· ${t.monteur}`}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => markDone(t)} data-testid={`termin-done-${t.id}`}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`termin-delete-${t.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="list">
          <Card className="border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Uhrzeit</TableHead>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Anlagentyp</TableHead>
                    <TableHead>Monteur</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground" data-testid="termine-empty">
                      Noch keine Zählertermine.
                    </TableCell></TableRow>
                  ) : items.map((t) => (
                    <TableRow key={t.id} data-testid={`termin-row-${t.id}`}>
                      <TableCell>{t.termin_datum}</TableCell>
                      <TableCell>{t.uhrzeit || "—"}</TableCell>
                      <TableCell>{t.kunde_name}</TableCell>
                      <TableCell>{ANLAGEN_TYPEN[t.anlagen_typ]}</TableCell>
                      <TableCell>{t.monteur || "—"}</TableCell>
                      <TableCell>
                        {t.erledigt ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600">Erledigt</Badge>
                        ) : (
                          <Badge variant="outline">Offen</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => markDone(t)} data-testid={`list-done-${t.id}`}><Check className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`list-delete-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
